/**
 * Partnerships List Page
 *
 * DataTable with full ListPageToolbar for partnerships + pending invitations section.
 */

import { useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, Building2, Loader2, ChevronRight, MapPin } from 'lucide-react'
import { Badge } from '@/shared/components/ui/badge'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/shared/components/ui/tabs'
import { NetworkAttentionPanel } from '@/admin/pages/partnerships/network-attention-panel'
import { NetworkDuplicatesPanel } from '@/admin/pages/partnerships/network-duplicates-panel'
import {
  DataTable,
  type DataTableColumn,
  type ActiveFilter,
} from '@/admin/components/data-table'
import { ListPageToolbar } from '@/admin/components/list-page-toolbar'
import { filterChipsSlice, useSavedListView } from '@/admin/hooks/use-saved-list-view'
import { useTableState } from '@/admin/hooks/use-table-state'
import { usePartnerships, usePartnershipDetail, usePartnershipInvitations, usePartnershipAttributes } from '@/admin/hooks/use-partnerships'
import { useExternalRoles } from '@/admin/hooks/use-external-roles'
import { useTableExport } from '@/admin/hooks/use-table-export'
import { useOrg } from '@/admin/hooks/use-org'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { formatDisplayValue, defineField } from '@/shared/lib/forms'
import { PageInfo } from '@/admin/components/page-info'
import { InvitePartnerDialog } from '@/admin/pages/partnerships/invite-partner-dialog'
import api from '@/admin/lib/api'
import type {
  Partnership,
  PartnershipInvitation,
} from '@/admin/types/organisations'

import { toastApiError } from '@/shared/lib/api-error'
const TABLE_ID = 'partnerships'

/**
 * A partners-table row. An invitation is a partner who has not accepted yet,
 * so it belongs in the same table rather than a block below the paginator -
 * `invitation` is set on those rows and every cell branches on it.
 */
type PartnerTableRow = Partnership & { invitation?: PartnershipInvitation }

export function PartnershipsPage() {
  const { t } = useTranslation()
  const { orgUuid } = useOrg()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  const [inviteOpen, setInviteOpen] = useState(false)
  const [togglingUuid, setTogglingUuid] = useState<string | null>(null)
  const [expandedUuids, setExpandedUuids] = useState<Set<string>>(new Set())
  // null = "All" pill active. Otherwise holds an ExternalOrganisationRole
  // uuid and narrows the list to partnerships in that role.
  const [roleFilter, setRoleFilter] = useState<string | null>(null)

  const { data: externalRoles = [] } = useExternalRoles()
  // Tenant-defined partnership attribute schema -> one column per attribute
  // (12 Assured: town, phone, postcode, brands, address). Fully generic: any
  // tenant's own attributes surface, no per-tenant branch.
  const { data: partnershipAttributes = [], isFetched: attributesFetched } =
    usePartnershipAttributes()
  const { locale, currency } = useFormatting()

  const { page, pageSize, sorting, setPage, setPageSize, setSorting, queryParams } =
    useTableState(TABLE_ID)

  // A single character is not a search, it is a full scan wearing one: on the
  // supagard book "a" matched 7,408 of 7,461 partnerships, "o" 7,427, versus 3
  // for "mk" and 123 for "lex". Two characters collapses that; a third buys
  // almost nothing and would block real terms - "mk" is Milton Keynes. Two also
  // matches the threshold ResourceSearchInput already uses, so there is one
  // rule across the app rather than two.
  const MIN_SEARCH_CHARS = 2
  const searchTerm = search.trim()
  const searchIsActive = searchTerm.length >= MIN_SEARCH_CHARS

  const params = useMemo(() => {
    const p: Record<string, string> = { ...queryParams }
    if (searchIsActive) p.search = searchTerm
    if (roleFilter) p.role = roleFilter
    activeFilters.forEach((f) => {
      p[f.field] = f.value
    })
    return p
  }, [queryParams, searchTerm, searchIsActive, roleFilter, activeFilters])

  const totalPartnerCount = useMemo(
    () => externalRoles.reduce((n, r) => n + (r.partnership_count ?? 0), 0),
    [externalRoles],
  )

  const { data: partnershipsData, isLoading, isFetching, isError, refetch } = usePartnerships(params)
  const { data: invitations = [], refetch: refetchInvitations } = usePartnershipInvitations()

  const totalCount = partnershipsData?.count ?? 0

  function toggleExpanded(uuid: string) {
    setExpandedUuids((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  }

  async function toggleActive(p: Partnership) {
    setTogglingUuid(p.uuid)
    const action = p.is_active ? 'suspend' : 'reactivate'
    try {
      await api.post(`/organisations/${orgUuid}/partnerships/${p.uuid}/${action}/`)
      toast.success(p.is_active ? t('partnerships.suspended') : t('partnerships.reactivated'))
      queryClient.invalidateQueries({ queryKey: ['partnerships'] })
    } catch (err) {
      toastApiError(err, t('partnerships.failedToAction'));
    } finally {
      setTogglingUuid(null)
    }
  }

  async function cancelInvitation(uuid: string) {
    try {
      await api.delete(`/organisations/${orgUuid}/partnership-invitations/${uuid}/`)
      toast.success(t('partnerships.invitationCancelled'))
      refetchInvitations()
    } catch (err) {
      toastApiError(err, t('partnerships.failedToCancel'));
    }
  }

  async function resendInvitation(uuid: string) {
    try {
      const { data } = await api.post(`/organisations/${orgUuid}/partnership-invitations/${uuid}/resend/`)
      if (data?.email_sent === false) {
        toast.warning('Invitation could not be sent. Please check your email configuration.')
      } else {
        toast.success(t('partnerships.invitationResent'))
      }
      // Resend moves the expiry, so an EXPIRED row becomes PENDING. Without
      // this the badge keeps saying EXPIRED and the operator cannot tell the
      // resend worked - on exactly the row they came here to fix.
      refetchInvitations()
    } catch (err) {
      toastApiError(err, t('partnerships.failedToResend'));
    }
  }

  const columns: DataTableColumn<PartnerTableRow>[] = useMemo(
    () => {
      // One opt-in column per tenant-defined partnership attribute. Hidden by
      // default - the standard view keeps Organisation / Role / Status / Users -
      // so an admin turns on the ones they want via COLUMNS and a saved view
      // persists the choice. Values come from the row's attribute_summary map,
      // formatted by field_type (so an ADDRESS renders as one line); invitation
      // rows carry no attributes, so they read as empty.
      const attributeColumns: DataTableColumn<PartnerTableRow>[] =
        partnershipAttributes.map((attr) => {
          // Build the field descriptor once per column, not once per cell
          // render (the table can render thousands of cells).
          const field = defineField({
            code_name: attr.code_name,
            display_name: attr.display_name,
            field_type: attr.field_type,
            configuration_data: attr.configuration_data,
          })
          return {
            id: `attr:${attr.code_name}`,
            header: attr.display_name,
            defaultVisible: false,
            cell: (row: PartnerTableRow) => {
              if (row.invitation) {
                return <span className="text-[13px] text-[var(--ink-3)]">-</span>
              }
              return (
                <span className="text-[13px] text-[var(--ink-2)]">
                  {formatDisplayValue(
                    field,
                    row.attribute_summary?.[attr.code_name],
                    { locale, currency },
                  )}
                </span>
              )
            },
          }
        })
      return [
      {
        id: 'organisation',
        header: 'Organisation',
        sortable: true,
        sortField: 'downstream_organisation__name',
        cell: (row) => {
          if (row.invitation) {
            return (
              <div className="flex items-center gap-2">
                <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <Building2 className="h-3.5 w-3.5 text-[var(--ink-3)]" />
                </div>
                <div className="min-w-0">
                  <span className="block truncate text-[13px] text-[var(--ink)]">
                    {row.invitation.organisation_name}
                  </span>
                  <span className="block truncate text-[11px] text-[var(--ink-3)]">
                    {row.invitation.admin_email}
                  </span>
                </div>
                <Badge
                  variant="outline"
                  className="ml-1 h-4 shrink-0 px-1.5 py-0 text-[9px] uppercase tracking-[0.06em] text-[var(--ink-3)]"
                >
                  {t('partnerships.matchInvited', 'Invited')}
                </Badge>
              </div>
            )
          }
          const isParent = row.child_partnership_count > 0
          const isExpanded = expandedUuids.has(row.uuid)
          return (
            <div className="flex items-center gap-2">
              {isParent ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleExpanded(row.uuid)
                  }}
                  className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors duration-100 hover:bg-[var(--bg-subtle)]"
                >
                  <ChevronRight
                    className={`h-3.5 w-3.5 text-[var(--ink-3)] transition-transform duration-150 ${isExpanded ? 'rotate-90' : ''}`}
                  />
                </button>
              ) : (
                <div className="flex h-5 w-5 shrink-0 items-center justify-center">
                  <Building2 className="h-3.5 w-3.5 text-[var(--ink-3)]" />
                </div>
              )}
              <span className={`truncate text-[13px] ${isParent ? 'font-medium text-[var(--ink)]' : 'text-[var(--ink)]'}`}>
                {row.downstream_organisation.name}
              </span>
              {isParent && (
                <span className="ml-1 shrink-0 rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] font-[500] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  {row.child_partnership_count}
                </span>
              )}
              {isParent && (
                <Badge variant="outline" className="ml-1 h-4 text-[9px] uppercase tracking-[0.06em] px-1.5 py-0">
                  {t('partnerships.parent', 'Head office')}
                </Badge>
              )}
              {!isParent && row.downstream_organisation.parent_organisation_name && (
                <Badge variant="outline" className="ml-1 h-4 text-[9px] uppercase tracking-[0.06em] px-1.5 py-0">
                  {t('partnerships.subsidiary', 'Location')}
                </Badge>
              )}
              {/* Why this row matched an email search. The email sources are
                  matched WITHOUT an is_deleted gate on purpose (a former
                  member's address is still an address people hold), so a
                  partner the person was already removed from came back looking
                  identical to one they are on - the users count was the only
                  tell. Only rendered for a non-active match: "active" is the
                  unsurprising case and needs no badge. */}
              {row.match_state === 'former' && (
                <Badge
                  variant="outline"
                  className="ml-1 h-4 border-[var(--status-warn)] px-1.5 py-0 text-[9px] uppercase tracking-[0.06em] text-[var(--status-warn)]"
                >
                  {t('partnerships.matchFormer', 'Former')}
                </Badge>
              )}
              {row.match_state === 'invited' && (
                <Badge
                  variant="outline"
                  className="ml-1 h-4 px-1.5 py-0 text-[9px] uppercase tracking-[0.06em] text-[var(--ink-3)]"
                >
                  {t('partnerships.matchInvited', 'Invited')}
                </Badge>
              )}
            </div>
          )
        },
      },
      {
        id: 'role',
        header: 'Role',
        sortable: true,
        sortField: 'role__name',
        cell: (row) => (
          <span className="text-[13px] text-[var(--ink-2)]">
            {row.role_name}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        sortField: 'is_active',
        meta: {
          filterType: 'select' as const,
          filterField: 'is_active',
          filterOptions: [
            { value: 'true', label: 'Active' },
            { value: 'false', label: 'Suspended' },
          ],
        },
        cell: (row) => {
          const inv = row.invitation
          const tone = inv
            ? inv.expired
              ? 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
              : 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
            : row.is_active
              ? 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]'
              : 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
          const label = inv
            ? inv.expired
              ? 'Expired'
              : 'Pending'
            : row.is_active
              ? 'Active'
              : 'Suspended'
          return (
            <span
              className={`inline-block rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] ${tone}`}
            >
              {label}
            </span>
          )
        },
      },
      {
        id: 'users',
        header: 'Users',
        cell: (row) => (
          <span className="text-[13px] text-[var(--ink-2)]">
            {row.invitation ? '-' : row.total_user_access_count}
          </span>
        ),
      },
      ...attributeColumns,
      {
        id: 'actions',
        header: '',
        cell: (row) =>
          row.invitation ? (
            <div className="flex items-center gap-1">
              {/* Expired included on purpose: resend RESETS token and expiry,
                  so it is the only way out of an expired invitation - an
                  expired one cannot be accepted. */}
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  resendInvitation(row.uuid)
                }}
                className="cursor-pointer rounded-[4px] px-2 py-1 text-[10px] uppercase tracking-[0.1em] text-[var(--green)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
              >
                Resend
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  cancelInvitation(row.uuid)
                }}
                className="cursor-pointer rounded-[4px] px-2 py-1 text-[10px] uppercase tracking-[0.1em] text-[var(--status-error)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
              >
                Cancel
              </button>
            </div>
          ) : (
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation()
                toggleActive(row)
              }}
              disabled={togglingUuid === row.uuid}
              className={`cursor-pointer rounded-[4px] px-2 py-1 text-[10px] uppercase tracking-[0.1em] transition-all duration-150 hover:bg-[var(--bg-subtle)] disabled:opacity-50 ${
                row.is_active ? 'text-[var(--status-warn)]' : 'text-[var(--green)]'
              }`}
            >
              {togglingUuid === row.uuid ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : row.is_active ? 'Suspend' : 'Reactivate'}
            </button>
          </div>
          ),
      },
      ]
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [togglingUuid, expandedUuids, partnershipAttributes, locale, currency],
  )

  const { exportCsv, isExporting } = useTableExport({
    endpoint: `/organisations/${orgUuid}/partnerships/`,
    columns,
    columnVisibility,
    params,
  })

  // Saved views hold columns, order, sort, the filter chips and the role pill.
  // With no view of their own, a user lands on the organisation default: it
  // carries which attribute columns a tenant wants up front, so it waits for
  // the attribute schema before opening.
  const savedView = useSavedListView({
    tableId: TABLE_ID,
    columns,
    columnVisibility,
    setColumnVisibility,
    saveColumnVisibility: setColumnVisibility,
    applyDefaultColumnPreferences: () => {
      setColumnVisibility({})
      setColumnOrder([])
    },
    columnOrder,
    setColumnOrder,
    saveColumnOrder: setColumnOrder,
    urlStateKeys: ['tab'],
    openOrgDefault: { ready: attributesFetched },
    slices: {
      sorting: { value: sorting ?? null, apply: setSorting, empty: null },
      filters: filterChipsSlice(activeFilters, setActiveFilters, columns),
      status: { value: roleFilter, apply: setRoleFilter, empty: null },
    },
  })

  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = searchParams.get('tab') ?? 'partners'
  // The invitations endpoint takes no search term, so a term has to be applied
  // here or every pending invitation pins itself to the top of every result -
  // searching one dealer group returned unrelated invited orgs above it.
  // Matched on the two fields the row actually renders.
  const pendingInvitations = useMemo(() => {
    const open = invitations.filter((inv) => !inv.accepted)
    if (!searchIsActive) return open
    const needle = searchTerm.toLowerCase()
    return open.filter(
      (inv) =>
        (inv.organisation_name ?? '').toLowerCase().includes(needle) ||
        (inv.admin_email ?? '').toLowerCase().includes(needle),
    )
  }, [invitations, searchIsActive, searchTerm])

  // Invitations are partners who have not accepted, so they lead the table
  // rather than sitting under it. Only on page 1: they are few and always
  // actionable, and the paginator still counts partnerships alone.
  const tableRows: PartnerTableRow[] = useMemo(() => {
    const invitationRows: PartnerTableRow[] = pendingInvitations.map((inv) => ({
      uuid: inv.uuid,
      downstream_organisation: {
        uuid: '',
        name: inv.organisation_name,
        slug: '',
        parent_organisation_uuid: null,
        parent_organisation_name: null,
      },
      role_uuid: '',
      role_name: inv.role_name,
      user_management_strategy: inv.user_management_strategy,
      downstream_mfa_required: inv.downstream_mfa_required,
      is_active: false,
      total_user_access_count: 0,
      child_partnership_count: 0,
      registration_count: 0,
      created_at: inv.created_at,
      invitation: inv,
    }))
    const rows = partnershipsData?.results ?? []
    return page === 1 ? [...invitationRows, ...rows] : rows
  }, [pendingInvitations, partnershipsData, page])

  return (
    <div>
      <main>
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-3">
          <div>
            <h1 className="clara-heading">{t('partnerships.title')}</h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="clara-small">
                {t('partnerships.subtitle')}
              </p>
              <PageInfo>
                {t('partnerships.description')}
              </PageInfo>
            </div>
          </div>
          <button
            data-testid="partners-add-btn"
            onClick={() => setInviteOpen(true)}
            className="px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] rounded-[4px] transition-all duration-150 cursor-pointer flex items-center gap-1.5 bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
          >
            <Plus className="h-3 w-3" />
            Invite Partner
          </button>
        </div>

        {/* The tab lives in the URL so a flow can send someone back to the
            queue they came from - dismissing a duplicate returns to Duplicates,
            not to a partner page they never asked for. */}
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            setSearchParams(
              value === 'partners' ? {} : { tab: value },
              { replace: true },
            )
          }}
        >
          <TabsList variant="line" className="mb-6">
            <TabsTrigger value="partners" data-testid="tab-partners">
              {t('network.tabs.partners', 'Partners')}
            </TabsTrigger>
            <TabsTrigger value="duplicates" data-testid="tab-duplicates">
              {t('network.tabs.duplicates', 'Duplicates')}
            </TabsTrigger>
            <TabsTrigger value="attention" data-testid="tab-attention">
              {t('network.tabs.attention', 'Needs attention')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="duplicates">
            <NetworkDuplicatesPanel
              onOpenPartner={(uuid) => navigate(`/network/${uuid}`)}
              onReviewGroup={(group) => {
                // Straight into the merge with the scan's own answer: keep the
                // suggested survivor, tick everything else in the group. The
                // operator still confirms, and can change the selection there.
                const survivor = group.suggested_survivor_uuid
                if (!survivor) return
                const others = group.members
                  .map((m) => m.partnership_uuid)
                  .filter((u): u is string => Boolean(u) && u !== survivor)
                navigate(
                  `/network/${survivor}/merge?preselect=${others.join(',')}`,
                )
              }}
            />
          </TabsContent>

          <TabsContent value="attention">
            <NetworkAttentionPanel onOpenPartner={(uuid) => navigate(`/network/${uuid}`)} />
          </TabsContent>

          <TabsContent value="partners">
        {/* Role filter pills */}
        {externalRoles.length > 0 && (
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <RoleFilterPill
              label="All"
              count={totalPartnerCount}
              active={roleFilter === null}
              onClick={() => {
                savedView.change('status', null)
                setPage(1)
              }}
            />
            {externalRoles.map((role) => (
              <RoleFilterPill
                key={role.uuid}
                label={role.name}
                count={role.partnership_count ?? 0}
                active={roleFilter === role.uuid}
                onClick={() => {
                  savedView.change('status', role.uuid)
                  setPage(1)
                }}
              />
            ))}
          </div>
        )}

        {/* Partners table */}
        <section className="mb-10">
          <ListPageToolbar<Partnership>
            tableId={TABLE_ID}
            search={search}
            onSearchChange={(v) => { setSearch(v); setPage(1) }}
            searchPlaceholder="Search partners..."
            {...savedView.toolbarProps}
            columns={columns}
            activeFilters={activeFilters}
            onFiltersChange={(filters) => {
              savedView.change(
                'filters',
                filters.map(({ field, operator, value }) => ({ field, operator, value })),
              )
              setPage(1)
            }}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            onExport={exportCsv}
            isExporting={isExporting}
          />

          <DataTable<PartnerTableRow>
            tableId={TABLE_ID}
            columns={columns}
            data={tableRows}
            totalCount={totalCount}
            isLoading={isLoading}
            isFetching={isFetching}
            isError={isError}
            onRetry={refetch}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            sorting={sorting}
            columnOrder={columnOrder}
            onSortingChange={(next) => savedView.change('sorting', next)}
            onRowClick={(row) => {
              // An invitation has no partnership to open yet.
              if (row.invitation) return
              navigate(`/network/${row.uuid}`)
            }}
            rowTestId={(row) => `partners-item-${row.uuid}`}
            emptyStateNode={
              // A typed-but-too-short term has not been searched yet, so the
              // "no partnerships" copy would be a false statement of fact -
              // exactly what the empty-state rule forbids. Say what is needed
              // instead.
              searchTerm.length > 0 && !searchIsActive ? (
                <EmptyState
                  icon={Building2}
                  title={t(
                    'partnerships.searchMinTitle',
                    'Keep typing to search',
                  )}
                  description={t(
                    'partnerships.searchMinDescription',
                    'Enter at least 2 characters to search partners.',
                  )}
                />
              ) : (
                <EmptyState
                  icon={Building2}
                  title={t('partnerships.emptyState', 'No partnerships yet')}
                  description={t(
                    'partnerships.emptyStateDescription',
                    'Invite a partner to get started.',
                  )}
                />
              )
            }
            columnVisibility={columnVisibility}
            renderRowDetail={(row) =>
              row.child_partnership_count > 0 && expandedUuids.has(row.uuid)
                ? <ChildPartnershipsPanel partnershipUuid={row.uuid} onNavigate={navigate} />
                : null
            }
          />
        </section>
          </TabsContent>
        </Tabs>

        {/* Invite dialog */}
        <InvitePartnerDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          onCreated={() => {
            queryClient.invalidateQueries({ queryKey: ['partnerships'] })
            refetchInvitations()
          }}
        />
      </main>
    </div>
  );
}


function ChildPartnershipsPanel({
  partnershipUuid,
  onNavigate,
}: {
  partnershipUuid: string
  onNavigate: (path: string) => void
}) {
  const { data: detail, isLoading } = usePartnershipDetail(partnershipUuid)
  const children = detail?.child_partnerships ?? []

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 pl-10">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--ink-3)]" />
        <span className="text-[12px] text-[var(--ink-3)]">Loading locations...</span>
      </div>
    )
  }

  if (children.length === 0) return null

  return (
    <div className="py-1 pl-10 pr-4">
      {children.map((child, idx) => (
        <div
          key={child.uuid}
          onClick={(e) => {
            e.stopPropagation()
            onNavigate(`/network/${child.uuid}`)
          }}
          className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 transition-colors duration-100 hover:bg-[var(--bg-subtle)] ${
            idx < children.length - 1 ? 'border-b-[0.5px] border-b-[var(--rule-light)]' : ''
          }`}
        >
          <MapPin className="h-3.5 w-3.5 shrink-0 text-[var(--ink-4)]" />
          <span className="truncate text-[13px] text-[var(--ink-2)]">
            {child.downstream_organisation.name}
          </span>
          {child.is_active === false && (
            <span className="shrink-0 rounded-full bg-[var(--status-error-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--status-error)]">
              Suspended
            </span>
          )}
          {child.attribute_summary && Object.keys(child.attribute_summary).length > 0 && (
            <span className="truncate text-[11px] text-[var(--ink-4)]">
              {Object.values(child.attribute_summary).filter(Boolean).join(' · ')}
            </span>
          )}
          <ChevronRight className="ml-auto h-3.5 w-3.5 shrink-0 text-[var(--ink-4)]" />
        </div>
      ))}
    </div>
  )
}


interface RoleFilterPillProps {
  label: string
  count: number
  active: boolean
  onClick: () => void
}

function RoleFilterPill({ label, count, active, onClick }: RoleFilterPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full border-[0.5px] px-3 py-1 text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer ${
        active
          ? 'border-[var(--green)] bg-[var(--green-bg)] text-[var(--green)]'
          : 'border-[var(--rule)] bg-[var(--bg)] text-[var(--ink-2)] hover:bg-[var(--bg-subtle)]'
      }`}
    >
      <span>{label}</span>
      <span
        className={`rounded-full px-1.5 text-[9px] font-[500] ${
          active
            ? 'bg-[var(--green)]/15 text-[var(--green)]'
            : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
        }`}
      >
        {count}
      </span>
    </button>
  )
}
